import { useEffect, useState } from "react";
import { Card } from "@/src/components/wrappers/Card";
import { useFetch } from "@/src/lib/useFetch";
import { Send, Eye, Heart, MessageSquare, CheckCircle2, FileCheck } from "lucide-react";

/**
 * Sent-offer engagement timeline (Phase 1, A4) — reads the offer_event log
 * (sent / each open / reactions / feedback / accepted / converted) written by
 * the client offer page and the send/convert actions.
 */

type OfferEvent = {
  id: string;
  type: "sent" | "opened" | "reacted" | "feedback" | "accepted" | "converted";
  metadata: Record<string, unknown> | null;
  createdAt: string;
};

const EVENT_ICONS: Record<OfferEvent["type"], JSX.Element> = {
  sent: <Send size={15} className="text-blue-600" />,
  opened: <Eye size={15} className="text-slate-500" />,
  reacted: <Heart size={15} className="text-rose-500" />,
  feedback: <MessageSquare size={15} className="text-amber-600" />,
  accepted: <CheckCircle2 size={15} className="text-emerald-600" />,
  converted: <FileCheck size={15} className="text-emerald-700" />,
};

function eventLabel(e: OfferEvent): string {
  const m = e.metadata ?? {};
  switch (e.type) {
    case "sent":
      return "Offer sent";
    case "opened":
      return "Client opened the offer";
    case "reacted": {
      const r = (m.reaction as string) ?? "";
      const y = (m.yachtName as string) ?? "";
      const icon = r === "love" ? "❤" : r === "maybe" ? "◐" : r === "pass" ? "✗" : "";
      return `Client reacted ${icon} ${r}${y ? ` — ${y}` : ""}`;
    }
    case "feedback":
      return "Client sent feedback";
    case "accepted":
      return "Client accepted a yacht";
    case "converted":
      return "Converted to booking";
    default:
      return e.type;
  }
}

function timeAgo(iso: string): string {
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

export function EngagementTimeline({ offerId }: { offerId: string }) {
  const [events, setEvents] = useState<OfferEvent[] | null>(null);
  const { fetch } = useFetch();

  useEffect(() => {
    if (!offerId) return;
    fetch<{ data: OfferEvent[] }>(`/api/offers/${offerId}/events`)
      .then((r) => setEvents(r.data ?? []))
      .catch(() => setEvents([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerId]);

  return (
    <Card className="flex flex-col basis-[664px] grow gap-4">
      <h3 className="text-lg font-semibold">Engagement</h3>
      {events === null ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : events.length === 0 ? (
        <p className="text-sm text-slate-500">No activity yet — events appear when the offer is sent and opened.</p>
      ) : (
        <ol className="flex flex-col gap-3 max-h-[320px] overflow-y-auto pr-1">
          {events.map((e) => (
            <li key={e.id} className="flex items-start gap-2.5 text-sm">
              <span className="mt-0.5 shrink-0">{EVENT_ICONS[e.type] ?? EVENT_ICONS.opened}</span>
              <span className="grow">
                {eventLabel(e)}
                {e.type === "feedback" && (e.metadata?.note as string) && (
                  <span className="block text-slate-500 text-xs mt-0.5">“{String(e.metadata!.note)}”</span>
                )}
              </span>
              <span className="text-xs text-slate-400 shrink-0 whitespace-nowrap">{timeAgo(e.createdAt)}</span>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}

export function ReactionChip(props: { reaction?: string | null; note?: string | null }) {
  if (!props.reaction) return null;
  const styles: Record<string, string> = {
    love: "bg-rose-50 border-rose-200 text-rose-700",
    maybe: "bg-amber-50 border-amber-200 text-amber-700",
    pass: "bg-slate-100 border-slate-200 text-slate-600",
  };
  const icons: Record<string, string> = { love: "❤", maybe: "◐", pass: "✗" };
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium ${styles[props.reaction] ?? styles.pass}`}
      title={props.note ?? undefined}
    >
      {icons[props.reaction] ?? ""} Client: {props.reaction}
      {props.note && <span className="font-normal text-slate-500 max-w-[280px] truncate">— “{props.note}”</span>}
    </span>
  );
}
